import { useRef, useState, type ReactNode } from 'react';

import {
  AGENT_CARD_BUTTON_DARK,
  AGENT_CARD_BUTTON_DESTRUCTIVE,
  AGENT_CARD_BUTTON_OUTLINE,
  CARD_LINK,
} from '@/components/agent-chat/agent-card.component';
import { useBuyCredits } from '@/components/agent-chat/use-buy-credits.hook';
import { AppIcon } from '@/components/ui/app-icon';
import { CreditAmount } from '@/components/ui/chips/credit-amount';
import { iconSpinnerThird } from '@/lib/icons';
import { cn } from '@/lib/utils';

/**
 * The parts every Clu card is built from (the card standard, CLE-606): the
 * main sentence, one gray line, rows with a cost at the right, a cost in the
 * header, the not-enough-credits line, and the footer with links on the left
 * and at most two buttons on the right. Credits are always the coin and a
 * number (CreditAmount), never the word.
 */

/** The card's one main sentence: 14/20, at most two lines by default. */
export function CardText({
  children,
  lines = 2,
  className,
}: {
  children: ReactNode;
  /** 0 never cuts it (a send preview, a question). */
  lines?: 0 | 1 | 2 | 3;
  className?: string;
}) {
  return (
    <p
      className={cn(
        'text-[14px] leading-[20px] text-foreground',
        lines === 1 && 'line-clamp-1',
        lines === 2 && 'line-clamp-2',
        lines === 3 && 'line-clamp-3',
        className
      )}
    >
      {children}
    </p>
  );
}

/** The one gray line under the sentence: counts, a schedule, a note. */
export function CardMeta({
  children,
  tone,
  className,
}: {
  children: ReactNode;
  tone?: 'warning' | 'danger';
  className?: string;
}) {
  return (
    <p
      className={cn(
        'text-[12px] leading-[16px] text-muted-foreground',
        tone === 'warning' && 'text-warning',
        tone === 'danger' && 'text-destructive',
        className
      )}
    >
      {children}
    </p>
  );
}

/**
 * A cost: "Up to", the coin, the number, a short unit ("/lead", "/week").
 * Amber when the balance can't cover it. Nothing for a free action: the
 * header then shows nothing, and a row shows Free (CardFree).
 */
export function CardCost({
  amount,
  upTo = false,
  about = false,
  plus = false,
  unit,
  short = false,
  exact = false,
  className,
}: {
  amount: number;
  /** Up to six decimals (a Try quote), not two. */
  exact?: boolean;
  upTo?: boolean;
  about?: boolean;
  /** Credits given, as a first-run start's "+50". */
  plus?: boolean;
  unit?: 'lead' | 'record' | 'company' | 'run' | 'week' | 'month' | 'day';
  short?: boolean;
  className?: string;
}) {
  return (
    <span
      data-card-cost
      className={cn(
        'inline-flex shrink-0 items-center gap-[4px] text-[12px] leading-[16px] font-medium whitespace-nowrap text-foreground',
        short && 'text-warning',
        className
      )}
    >
      {upTo ? (
        <span className='font-normal text-muted-foreground'>Up to</span>
      ) : null}
      {about ? (
        <span className='font-normal text-muted-foreground'>About</span>
      ) : null}
      {plus ? <span>+</span> : null}
      <CreditAmount amount={amount} maxDecimals={exact ? 6 : 2} />
      {unit ? (
        <span className='font-normal text-muted-foreground'>/{unit}</span>
      ) : null}
    </span>
  );
}

/** A row's "Free", where a cost would sit. */
export function CardFree() {
  return (
    <span className='text-[12px] leading-[16px] text-muted-foreground'>
      Free
    </span>
  );
}

/** A status at the right of a header: Cancelled, Accepted, Off, Draft. */
export function CardStatus({
  children,
  tone,
}: {
  children: ReactNode;
  tone?: 'success' | 'warning' | 'danger';
}) {
  return (
    <span
      className={cn(
        'text-[12px] leading-[16px] whitespace-nowrap text-muted-foreground',
        tone === 'success' && 'text-success',
        tone === 'warning' && 'text-warning',
        tone === 'danger' && 'text-destructive'
      )}
    >
      {children}
    </span>
  );
}

/** Rows stacked 4px apart. */
export function CardRows({
  children,
  label,
  selectable = false,
}: {
  children: ReactNode;
  /** The group's name for a screen reader. */
  label?: string;
  /** The rows are a single choice (radio rows). */
  selectable?: boolean;
}) {
  return (
    <div
      role={selectable ? 'radiogroup' : undefined}
      aria-label={label}
      className='flex flex-col gap-[4px]'
    >
      {children}
    </div>
  );
}

/**
 * One row: a label at the left, a value or a cost at the right. With
 * `onSelect` it is a radio choice (the import's enrichment, a list to use).
 * `wrap` shows a long label and value in full instead of cutting them (what
 * a confirm agrees to).
 */
export function CardRow({
  label,
  end,
  selected,
  onSelect,
  disabled,
  wrap = false,
}: {
  label: ReactNode;
  end?: ReactNode;
  selected?: boolean;
  onSelect?: () => void;
  disabled?: boolean;
  wrap?: boolean;
}) {
  const className = cn(
    'flex w-full min-w-0 items-center justify-between gap-[8px] rounded-[8px] border px-[10px] py-[6px] text-left text-[12px] leading-[16px] text-foreground',
    selected
      ? 'border-foreground/60 bg-muted/60'
      : 'border-border bg-background',
    onSelect && !selected && 'hover:bg-muted/60'
  );
  const content = (
    <>
      <span
        className={cn(
          'min-w-0 font-medium',
          wrap ? '[overflow-wrap:anywhere] whitespace-pre-wrap' : 'truncate'
        )}
      >
        {label}
      </span>
      {end ? (
        <span
          className={cn(
            'flex items-center text-muted-foreground',
            wrap
              ? 'min-w-0 justify-end text-right [overflow-wrap:anywhere]'
              : 'shrink-0'
          )}
        >
          {end}
        </span>
      ) : null}
    </>
  );
  if (!onSelect) return <div className={className}>{content}</div>;
  return (
    <button
      type='button'
      role='radio'
      aria-checked={selected === true}
      disabled={disabled}
      onClick={onSelect}
      className={cn(className, 'transition-colors disabled:opacity-50')}
    >
      {content}
    </button>
  );
}

/**
 * The balance can't cover the card: what the org has, in amber, and what to
 * do. The card's main button becomes Buy credits (see CardFooter `short`).
 */
export function CardShortOfCredits({
  have,
  hint = 'Add credits to go on.',
  exact = false,
}: {
  have: number;
  hint?: string;
  /** Up to six decimals, beside a cost shown that way. */
  exact?: boolean;
}) {
  return (
    <CardMeta tone='warning'>
      <span className='inline-flex flex-wrap items-center gap-x-[4px]'>
        You have{' '}
        {/* The period stays against the amount, out of the row's gap. */}
        <span className='inline-flex items-center'>
          <CreditAmount
            amount={Math.max(0, have)}
            maxDecimals={exact ? 6 : 2}
          />
          .
        </span>{' '}
        {hint}
      </span>
    </CardMeta>
  );
}

/** What the card's main button does. */
export interface CardAction {
  label: string;
  /** Shown with a spinner once clicked: "Importing", "Turning on". */
  workingLabel?: string;
  /** May return a promise. A result of `false`, or `{ ok: false }` that
   *  wasn't aborted, unlocks the card so the user can try again. */
  onClick: () => unknown;
  tone?: 'danger';
  disabled?: boolean;
}

function failed(result: unknown): boolean {
  if (result === false) return true;
  if (result && typeof result === 'object' && 'ok' in result) {
    const r = result as { ok?: unknown; aborted?: unknown };
    return r.ok === false && r.aborted !== true;
  }
  return false;
}

/**
 * The card's last row: links at the left (Details, Canvas, Open list), a
 * control such as the import count, then Cancel and the main button. Once
 * the main button is clicked both lock and it shows a spinner with its
 * working label, so a second click can't send twice. `short` swaps the main
 * button for Buy credits (the credit dialog), keeping Cancel.
 */
export function CardFooter({
  links,
  control,
  cancel,
  primary,
  short,
  locked = false,
}: {
  links?: ReactNode;
  control?: ReactNode;
  cancel?: { label?: string; onClick: () => unknown };
  primary?: CardAction;
  /** The credits the card needs when the balance can't cover them. */
  short?: { needed?: number };
  /** Locked from outside (a confirm already sent). */
  locked?: boolean;
}) {
  const [working, setWorking] = useState(false);
  // Two clicks in the same tick both see `working` false; the ref doesn't.
  const sent = useRef(false);
  const buyCredits = useBuyCredits(short?.needed);
  const busy = working || locked;

  const unlock = () => {
    sent.current = false;
    setWorking(false);
  };

  const run = () => {
    if (!primary || busy || sent.current) return;
    sent.current = true;
    setWorking(true);
    void Promise.resolve()
      .then(() => primary.onClick())
      .then((result) => {
        if (failed(result)) unlock();
      })
      .catch(unlock);
  };

  return (
    <div className='flex flex-wrap items-center gap-[8px]'>
      {links ? (
        <div className='flex min-w-0 items-center gap-[12px]'>{links}</div>
      ) : null}
      {control}
      <div className='ml-auto flex items-center gap-[8px]'>
        {cancel ? (
          <button
            type='button'
            onClick={() => void cancel.onClick()}
            disabled={busy}
            className={AGENT_CARD_BUTTON_OUTLINE}
          >
            {cancel.label ?? 'Cancel'}
          </button>
        ) : null}
        {short ? (
          <button
            type='button'
            onClick={buyCredits}
            className={AGENT_CARD_BUTTON_DARK}
          >
            Buy credits
          </button>
        ) : primary ? (
          <button
            type='button'
            onClick={run}
            disabled={busy || primary.disabled}
            aria-busy={working || undefined}
            className={
              primary.tone === 'danger'
                ? AGENT_CARD_BUTTON_DESTRUCTIVE
                : AGENT_CARD_BUTTON_DARK
            }
          >
            {working ? (
              <>
                <AppIcon
                  icon={iconSpinnerThird}
                  spin
                  style={{ fontSize: 12 }}
                />
                {primary.workingLabel ?? primary.label}
              </>
            ) : (
              primary.label
            )}
          </button>
        ) : null}
      </div>
    </div>
  );
}

/** A card's text link as a button (Details, Review all, Undo). */
export function CardLinkButton({
  children,
  onClick,
  disabled,
}: {
  children: ReactNode;
  onClick: () => void;
  disabled?: boolean;
}) {
  return (
    <button
      type='button'
      onClick={onClick}
      disabled={disabled}
      className={CARD_LINK}
    >
      {children}
    </button>
  );
}
